import type { IndustrySlug } from "./industrySolutions";

export type IndustryVisualFamily = "acquisition" | "operations" | "service";

export interface IndustryWorkflowScenario {
  id: string;
  tab: string;
  title: string;
  inquiry: string;
  response: string;
  fields: Array<{ label: string; value: string }>;
  nextStep: {
    label: string;
    title: string;
    detail: string;
    status: string;
  };
  humanReview: string;
}

export interface IndustryPageDesign {
  family: IndustryVisualFamily;
  headline: string;
  description: string;
  primaryCta: string;
  secondaryCta: string;
  capabilities: [string, string, string];
  proof: [
    { label: string; title: string; body: string },
    { label: string; title: string; body: string },
    { label: string; title: string; body: string },
  ];
  workflowEyebrow: string;
  workflowTitle: string;
  workflowBody: string;
  scenarios: [IndustryWorkflowScenario, IndustryWorkflowScenario, IndustryWorkflowScenario];
  pilotSignals: [string, string, string];
  handoffRules: [string, string, string];
  finalTitle: string;
  finalBody: string;
}

export const industryPageDesigns: Record<IndustrySlug, IndustryPageDesign> = {
  "medical-aesthetics": {
    family: "acquisition",
    headline: "Turn treatment inquiries into consultation-ready next steps.",
    description: "ClawEase answers approved non-clinical questions, captures treatment interest, goals, timing, and contact preferences, and prepares a complete consultation handoff—while clinical decisions stay with qualified staff.",
    primaryCta: "Review my consultation workflow",
    secondaryCta: "See a sample inquiry",
    capabilities: ["Approved non-clinical answers", "Consultation-ready intake", "Clinical questions handed to staff"],
    proof: [
      { label: "What it captures", title: "Consultation context", body: "Treatment interest, goals, timing, location, and preferred contact channel." },
      { label: "What it can prepare", title: "A staff-ready request", body: "A structured consultation summary and an approved scheduling next step." },
      { label: "When a person takes over", title: "Clinical judgment", body: "Eligibility, treatment advice, contraindications, or any exceptional request." },
    ],
    workflowEyebrow: "Sample consultation workflow",
    workflowTitle: "From first treatment question to a safe next step.",
    workflowBody: "Switch between three fictional examples. The fields and actions shown here are approved during setup before anything connects to a production system.",
    scenarios: [
      {
        id: "treatment-inquiry",
        tab: "Treatment inquiry",
        title: "Answer an after-hours inquiry",
        inquiry: "I’m interested in a skin consultation. Do you have appointments after work?",
        response: "I can share approved service information and collect a few details for the consultation team.",
        fields: [
          { label: "Interest", value: "Skin consultation" },
          { label: "Goal", value: "Discuss texture concerns" },
          { label: "Timing", value: "Weekday evenings" },
          { label: "Contact", value: "Text message" },
        ],
        nextStep: { label: "Prepared next step", title: "Consultation request", detail: "Evening preference attached", status: "Ready for review" },
        humanReview: "Any question about suitability, risk, recovery, or treatment choice is routed to qualified clinic staff.",
      },
      {
        id: "consultation-booking",
        tab: "Consultation booking",
        title: "Match an approved consultation window",
        inquiry: "Thursday after 5 works best. Is the downtown location available?",
        response: "I can check the approved consultation schedule and prepare the selected time for confirmation.",
        fields: [
          { label: "Location", value: "Downtown" },
          { label: "Day", value: "Thursday" },
          { label: "Window", value: "After 5 PM" },
          { label: "Type", value: "Initial consultation" },
        ],
        nextStep: { label: "Prepared next step", title: "Thursday · 5:30 PM", detail: "Pending clinic confirmation", status: "Time selected" },
        humanReview: "Staff confirms any request that needs provider matching, special preparation, or schedule overrides.",
      },
      {
        id: "pre-care-handoff",
        tab: "Pre-care handoff",
        title: "Keep preparation and exceptions visible",
        inquiry: "I booked a consultation. Is there anything I should do before I arrive?",
        response: "I can send the clinic’s approved preparation information and pass clinical questions to the team.",
        fields: [
          { label: "Appointment", value: "Initial consultation" },
          { label: "Guide", value: "Approved instructions" },
          { label: "Forms", value: "Intake link prepared" },
          { label: "Exception", value: "Clinical question flagged" },
        ],
        nextStep: { label: "Prepared next step", title: "Staff follow-up task", detail: "Full conversation attached", status: "Human review" },
        humanReview: "Clinical preparation, medication, contraindication, and eligibility questions never receive an automated decision.",
      },
    ],
    pilotSignals: ["First-response time", "Consultation record completeness", "Requests reaching a confirmed next step"],
    handoffRules: ["Clinical or eligibility question", "Requested action is outside approved policy", "Customer asks for a person"],
    finalTitle: "Bring one repeated consultation request.",
    finalBody: "We’ll map the intake fields, approved actions, and clinical handoff boundary for a focused workflow review.",
  },
  "dental-clinics": {
    family: "acquisition",
    headline: "Turn new-patient calls into complete, schedulable requests.",
    description: "ClawEase captures visit reason, contact details, provider or location preference, timing constraints, and approved non-clinical fields, then prepares the request for scheduling or staff review.",
    primaryCta: "Review my front-desk workflow",
    secondaryCta: "See a new-patient example",
    capabilities: ["New-patient intake", "Scheduling context", "Urgent requests routed to staff"],
    proof: [
      { label: "What it captures", title: "A complete request", body: "Visit reason, patient status, location, timing, contact details, and approved intake fields." },
      { label: "What it can prepare", title: "Scheduling or rescheduling", body: "A suitable next step using the clinic’s approved availability and rules." },
      { label: "When a person takes over", title: "Urgent or clinical needs", body: "Symptoms, treatment advice, emergencies, or exceptions move to clinic staff." },
    ],
    workflowEyebrow: "Sample front-desk workflow",
    workflowTitle: "From a missed call risk to a complete patient request.",
    workflowBody: "Explore fictional front-desk examples without implying a real patient record or a production appointment.",
    scenarios: [
      {
        id: "new-patient-intake",
        tab: "New patient",
        title: "Collect a schedulable intake",
        inquiry: "I’m a new patient and would like to book a routine cleaning next week.",
        response: "I can collect the non-clinical details the front desk needs and prepare suitable options.",
        fields: [
          { label: "Patient", value: "New" },
          { label: "Visit reason", value: "Routine cleaning" },
          { label: "Location", value: "North clinic" },
          { label: "Availability", value: "Next week · mornings" },
        ],
        nextStep: { label: "Prepared next step", title: "New-patient request", detail: "Required fields complete", status: "Ready to schedule" },
        humanReview: "Pain, swelling, trauma, medication, or treatment questions are routed to clinic staff.",
      },
      {
        id: "appointment-options",
        tab: "Appointment options",
        title: "Offer only approved times",
        inquiry: "Tuesday or Wednesday morning would work. Do you have anything before 10?",
        response: "I can compare those preferences with the approved schedule and prepare an option.",
        fields: [
          { label: "Days", value: "Tue / Wed" },
          { label: "Window", value: "Before 10 AM" },
          { label: "Provider", value: "No preference" },
          { label: "Channel", value: "Phone confirmation" },
        ],
        nextStep: { label: "Prepared next step", title: "Wednesday · 9:15 AM", detail: "Pending final confirmation", status: "Option held" },
        humanReview: "Complex provider matching, schedule overrides, and policy exceptions require a front-desk decision.",
      },
      {
        id: "reschedule",
        tab: "Reschedule",
        title: "Preserve context when a time changes",
        inquiry: "I can’t make Friday. Can I move the visit to early next week?",
        response: "I’ll keep the original appointment context and prepare approved alternatives.",
        fields: [
          { label: "Current visit", value: "Friday · 2 PM" },
          { label: "Preference", value: "Mon / Tue morning" },
          { label: "Visit type", value: "Existing appointment" },
          { label: "Reminder", value: "Text requested" },
        ],
        nextStep: { label: "Prepared next step", title: "Reschedule request", detail: "Original visit linked", status: "Staff review" },
        humanReview: "The team takes over when rescheduling changes visit type, provider requirements, or clinic policy.",
      },
    ],
    pilotSignals: ["Calls receiving a useful response", "Required intake field completion", "Requests prepared for scheduling"],
    handoffRules: ["Urgent or clinical request", "No approved appointment matches", "Patient requests staff"],
    finalTitle: "Bring one repeated front-desk request.",
    finalBody: "We’ll review the fields, scheduling rules, and clinical escalation path your team needs.",
  },
  "pet-services": {
    family: "service",
    headline: "Turn pet-care requests into complete, staff-ready bookings.",
    description: "ClawEase captures owner and pet details, requested service, dates, handling notes, and approved vaccination fields, then prepares the booking or staff review with context intact.",
    primaryCta: "Review my pet-service workflow",
    secondaryCta: "See a booking example",
    capabilities: ["Owner and pet profile", "Grooming or boarding context", "Care exceptions handed to staff"],
    proof: [
      { label: "What it captures", title: "Pet-care context", body: "Pet profile, service, dates, handling notes, care preferences, and approved document fields." },
      { label: "What it can prepare", title: "A feasible service request", body: "A grooming, boarding, or follow-up request with the required context attached." },
      { label: "When a person takes over", title: "Health and care judgment", body: "Clinical questions, behavior risks, special care, or policy exceptions go to staff." },
    ],
    workflowEyebrow: "Sample pet-service workflow",
    workflowTitle: "Keep owner and pet context together from request to pickup.",
    workflowBody: "The examples below distinguish grooming, boarding, and owner updates instead of treating every pet-care request like a generic appointment.",
    scenarios: [
      {
        id: "pet-profile",
        tab: "Pet profile",
        title: "Build a staff-ready pet profile",
        inquiry: "I’d like to book grooming for my three-year-old golden retriever.",
        response: "I can collect the service details and care notes your team needs before confirming anything.",
        fields: [
          { label: "Pet", value: "Dog · Golden retriever" },
          { label: "Age", value: "3 years" },
          { label: "Service", value: "Full grooming" },
          { label: "Care note", value: "Sensitive paws" },
        ],
        nextStep: { label: "Prepared next step", title: "Grooming request", detail: "Pet profile attached", status: "Ready for review" },
        humanReview: "Behavior, health, vaccine, medication, or special-handling decisions are routed to pet-care staff.",
      },
      {
        id: "boarding-request",
        tab: "Boarding request",
        title: "Collect dates and care requirements",
        inquiry: "Do you have boarding available for a long weekend next month?",
        response: "I can collect the stay dates, pet profile, care notes, and approved document fields.",
        fields: [
          { label: "Stay", value: "Fri–Mon" },
          { label: "Pet", value: "One dog" },
          { label: "Care", value: "Twice-daily feeding" },
          { label: "Documents", value: "Review required" },
        ],
        nextStep: { label: "Prepared next step", title: "Boarding availability request", detail: "Care notes included", status: "Staff review" },
        humanReview: "Capacity, compatibility, vaccination acceptance, and special care are confirmed by staff.",
      },
      {
        id: "owner-update",
        tab: "Owner update",
        title: "Send an approved service milestone",
        inquiry: "Could you let me know when pickup is ready?",
        response: "I can send the approved pickup status and keep the service record attached.",
        fields: [
          { label: "Service", value: "Grooming" },
          { label: "Status", value: "Finishing checks" },
          { label: "Channel", value: "Text message" },
          { label: "Pickup", value: "Window requested" },
        ],
        nextStep: { label: "Prepared next step", title: "Pickup update", detail: "Approved message ready", status: "Ready to send" },
        humanReview: "Unexpected health, behavior, delay, or service-quality issues are handled by the team.",
      },
    ],
    pilotSignals: ["Profile completeness before service", "Booking requests needing fewer follow-ups", "Approved status updates sent"],
    handoffRules: ["Health or behavior concern", "Document or policy exception", "Owner asks for staff"],
    finalTitle: "Bring one repeated pet-care request.",
    finalBody: "We’ll map the pet profile, service action, and staff handoff without mixing clinical decisions into automation.",
  },
  "education-training": {
    family: "acquisition",
    headline: "Move course inquiries from question to booked next step.",
    description: "ClawEase answers from approved course information, captures learner goals, level, format, location, and availability, and prepares a trial, consultation, or admissions handoff.",
    primaryCta: "Review my enrollment workflow",
    secondaryCta: "See a learner inquiry",
    capabilities: ["Approved course answers", "Structured learner intake", "Admissions handoff with context"],
    proof: [
      { label: "What it captures", title: "A useful learner profile", body: "Goal, current level, format, location, availability, and preferred next step." },
      { label: "What it can prepare", title: "A trial or advisor request", body: "An approved class, consultation, or admissions handoff with context attached." },
      { label: "When a person takes over", title: "Placement and exceptions", body: "Placement judgment, financial decisions, and unusual requests stay with your team." },
    ],
    workflowEyebrow: "Sample enrollment workflow",
    workflowTitle: "From first question to a clear next step.",
    workflowBody: "Use the tabs to inspect three fictional learner scenarios. The example shows the structure of the workflow—not a real learner record or live school system.",
    scenarios: [
      {
        id: "course-inquiry",
        tab: "Course inquiry",
        title: "Answer from the approved catalog",
        inquiry: "Do you offer evening IELTS classes for someone around B1 level?",
        response: "Yes. I can share approved course options and collect your goal, format, location, and availability.",
        fields: [
          { label: "Goal", value: "IELTS 7.0" },
          { label: "Current level", value: "B1 · self-reported" },
          { label: "Format", value: "In person" },
          { label: "Availability", value: "Tue / Thu evenings" },
        ],
        nextStep: { label: "Prepared next step", title: "Course match review", detail: "Approved catalog source", status: "Ready for advisor" },
        humanReview: "Formal placement, eligibility, pricing exceptions, and learning advice are handled by an advisor.",
      },
      {
        id: "trial-booking",
        tab: "Trial booking",
        title: "Match the learner to an approved trial",
        inquiry: "The downtown location works. Could I try the Tuesday evening class?",
        response: "I can prepare the approved trial option and send the registration step.",
        fields: [
          { label: "Course", value: "IELTS Foundation" },
          { label: "Location", value: "Downtown" },
          { label: "Trial", value: "Tuesday · 7:00 PM" },
          { label: "Contact", value: "Email" },
        ],
        nextStep: { label: "Prepared next step", title: "Trial class request", detail: "Learner profile attached", status: "Ready to confirm" },
        humanReview: "An advisor confirms capacity, placement, scholarships, and any request outside the approved schedule.",
      },
      {
        id: "enrollment-follow-up",
        tab: "Enrollment follow-up",
        title: "Keep the handoff traceable",
        inquiry: "I enjoyed the trial. What do I need to do next?",
        response: "I can send the approved enrollment checklist and prepare an advisor follow-up.",
        fields: [
          { label: "Trial", value: "Completed" },
          { label: "Course interest", value: "IELTS Foundation" },
          { label: "Checklist", value: "Approved steps" },
          { label: "Owner", value: "Admissions team" },
        ],
        nextStep: { label: "Prepared next step", title: "Admissions follow-up", detail: "Conversation and profile included", status: "Assigned" },
        humanReview: "Admissions owns placement, contract, payment, scholarship, and exception decisions.",
      },
    ],
    pilotSignals: ["Time to first useful answer", "Learner profile completeness", "Inquiries reaching a defined next step"],
    handoffRules: ["Placement or eligibility decision", "Financial or policy exception", "Learner requests an advisor"],
    finalTitle: "Bring one admissions workflow. Leave with a clear demo plan.",
    finalBody: "We’ll map the fields, approved system action, and human handoff for one repeated learner request.",
  },
  "field-services": {
    family: "operations",
    headline: "Turn every service request into a dispatch-ready job.",
    description: "ClawEase captures the location, issue, asset, urgency, access notes, supporting details, and preferred visit window your team needs before dispatch.",
    primaryCta: "Review my dispatch workflow",
    secondaryCta: "See a service-request example",
    capabilities: ["Complete service intake", "Dispatch-ready job record", "Customer status context"],
    proof: [
      { label: "What it captures", title: "Job-ready context", body: "Address, issue, asset, urgency, access, availability, and supporting details." },
      { label: "What it can prepare", title: "A dispatch-ready request", body: "A structured job with the fields needed for routing and scheduling review." },
      { label: "When a person takes over", title: "Safety and exceptions", body: "Hazards, emergencies, service-area exceptions, and judgment calls go to dispatch." },
    ],
    workflowEyebrow: "Sample dispatch workflow",
    workflowTitle: "From an unstructured request to owned field work.",
    workflowBody: "These fictional scenarios focus on work records, routing context, and status—not a generic appointment calendar.",
    scenarios: [
      {
        id: "service-request",
        tab: "Service request",
        title: "Capture the issue before dispatch",
        inquiry: "Our rooftop unit is running but no cool air is coming through.",
        response: "I can collect the location, equipment, symptoms, urgency, and access details for the service team.",
        fields: [
          { label: "Site", value: "Retail location" },
          { label: "Asset", value: "Rooftop HVAC" },
          { label: "Issue", value: "No cooling" },
          { label: "Access", value: "Roof key required" },
        ],
        nextStep: { label: "Prepared next step", title: "Service job record", detail: "Required intake complete", status: "Ready for dispatch" },
        humanReview: "Safety hazards, outages, emergencies, and ambiguous severity are escalated immediately.",
      },
      {
        id: "routing-context",
        tab: "Routing context",
        title: "Make routing rules visible",
        inquiry: "We need someone tomorrow morning. The unit is at our west-side store.",
        response: "I’ll attach the service area, required skill, urgency signal, and preferred visit window.",
        fields: [
          { label: "Area", value: "West service zone" },
          { label: "Skill", value: "Commercial HVAC" },
          { label: "Priority", value: "Dispatcher review" },
          { label: "Window", value: "Tomorrow morning" },
        ],
        nextStep: { label: "Prepared next step", title: "Routing recommendation", detail: "No technician assigned automatically", status: "Dispatch review" },
        humanReview: "Dispatch approves priority, technician assignment, travel, and schedule exceptions.",
      },
      {
        id: "status-update",
        tab: "Status update",
        title: "Keep the requester informed",
        inquiry: "Has someone been assigned, and when should we expect an update?",
        response: "I can share an approved status milestone and preserve the job context for follow-up.",
        fields: [
          { label: "Job", value: "Service request linked" },
          { label: "Status", value: "Dispatch review" },
          { label: "Channel", value: "Text message" },
          { label: "Next update", value: "After assignment" },
        ],
        nextStep: { label: "Prepared next step", title: "Requester update", detail: "Approved status only", status: "Ready to send" },
        humanReview: "Delays, revised estimates, safety issues, and customer complaints remain with the service team.",
      },
    ],
    pilotSignals: ["Required job-field completion", "Time from request to dispatch review", "Requester status contacts avoided"],
    handoffRules: ["Safety or emergency signal", "Routing rule has no valid match", "Requester needs dispatch"],
    finalTitle: "Bring one repeated service request.",
    finalBody: "We’ll map the intake, routing context, and dispatcher handoff needed to make it job-ready.",
  },
  "hospitality-rentals": {
    family: "service",
    headline: "Keep every guest request moving—from first question to the right team.",
    description: "ClawEase captures stay dates, party details, property preference, arrival context, and service needs, then prepares reservation, arrival, or in-stay requests for the correct next step.",
    primaryCta: "Review my guest-service workflow",
    secondaryCta: "See a guest-request example",
    capabilities: ["Guest and stay context", "Reservation-ready requests", "In-stay task handoff"],
    proof: [
      { label: "What it captures", title: "Stay context", body: "Dates, party, property, requirements, arrival details, and service needs." },
      { label: "What it can prepare", title: "The right guest request", body: "A reservation, arrival, housekeeping, maintenance, or amenity handoff." },
      { label: "When a person takes over", title: "Policy and guest recovery", body: "Payment, exceptions, safety, complaints, and recovery decisions stay with staff." },
    ],
    workflowEyebrow: "Sample guest-service workflow",
    workflowTitle: "Keep the context from reservation question to in-stay service.",
    workflowBody: "The examples separate reservation, arrival, and in-stay operations so each request reaches the right owner.",
    scenarios: [
      {
        id: "reservation-request",
        tab: "Reservation request",
        title: "Prepare a complete stay request",
        inquiry: "Do you have a two-bedroom property for four guests next month?",
        response: "I can collect the stay details and compare them with the approved availability source.",
        fields: [
          { label: "Stay", value: "Three nights · next month" },
          { label: "Party", value: "Four guests" },
          { label: "Property", value: "Two bedrooms" },
          { label: "Requirement", value: "Late arrival" },
        ],
        nextStep: { label: "Prepared next step", title: "Reservation request", detail: "Stay context complete", status: "Availability review" },
        humanReview: "Rates, payment, policy exceptions, guarantees, and special accommodations are confirmed by staff.",
      },
      {
        id: "arrival-coordination",
        tab: "Arrival",
        title: "Coordinate approved arrival details",
        inquiry: "Our flight lands at 9:30 PM. How do we get access after hours?",
        response: "I can share the property’s approved arrival steps and route any access exception.",
        fields: [
          { label: "Arrival", value: "9:30 PM" },
          { label: "Property", value: "Reservation linked" },
          { label: "Access", value: "Approved instructions" },
          { label: "Exception", value: "After-hours arrival" },
        ],
        nextStep: { label: "Prepared next step", title: "Arrival coordination", detail: "Guest context attached", status: "Staff notified" },
        humanReview: "Access failures, identity issues, refunds, rebooking, and safety concerns go to the property team.",
      },
      {
        id: "in-stay-request",
        tab: "In-stay request",
        title: "Route a guest need to the right owner",
        inquiry: "The bedroom air conditioner is making a loud noise.",
        response: "I can capture the room, issue, urgency, access preference, and the guest’s preferred update channel.",
        fields: [
          { label: "Request", value: "Maintenance" },
          { label: "Location", value: "Bedroom" },
          { label: "Access", value: "Contact before entry" },
          { label: "Update", value: "Message" },
        ],
        nextStep: { label: "Prepared next step", title: "Maintenance task", detail: "Guest request linked", status: "Ready to route" },
        humanReview: "Safety, relocation, compensation, and guest-recovery decisions remain with property staff.",
      },
    ],
    pilotSignals: ["Reservation-request completeness", "Time to correct task owner", "Approved guest updates delivered"],
    handoffRules: ["Safety, payment, or access exception", "Complaint or recovery decision", "Guest asks for staff"],
    finalTitle: "Bring one repeated guest request.",
    finalBody: "We’ll map the stay context, operational task, and staff handoff for one guest-service workflow.",
  },
  "fitness-wellness": {
    family: "acquisition",
    headline: "Turn membership interest into a booked first visit.",
    description: "ClawEase answers approved program and schedule questions, captures goals and preferences, and prepares the right trial, class, assessment, or consultation next step.",
    primaryCta: "Review my membership workflow",
    secondaryCta: "See a trial-booking example",
    capabilities: ["Approved program answers", "Goal and schedule intake", "Trial or staff handoff"],
    proof: [
      { label: "What it captures", title: "A useful prospect profile", body: "Goals, experience, program, location, schedule, and preferred first visit." },
      { label: "What it can prepare", title: "A first-visit request", body: "A trial, class, assessment, consultation, or waitlist next step." },
      { label: "When a person takes over", title: "Health and membership judgment", body: "Health advice, complex membership terms, and exceptions stay with staff." },
    ],
    workflowEyebrow: "Sample membership workflow",
    workflowTitle: "From membership question to a clear first visit.",
    workflowBody: "The examples show program discovery, trial booking, and follow-up without inventing a connected membership platform.",
    scenarios: [
      {
        id: "membership-inquiry",
        tab: "Membership inquiry",
        title: "Understand the prospect’s goal",
        inquiry: "I’m getting back into exercise. Which beginner-friendly options run after work?",
        response: "I can share approved program information and collect your goals, experience, location, and schedule.",
        fields: [
          { label: "Goal", value: "Build consistency" },
          { label: "Experience", value: "Returning beginner" },
          { label: "Location", value: "Downtown" },
          { label: "Schedule", value: "Weekday evenings" },
        ],
        nextStep: { label: "Prepared next step", title: "Program consultation", detail: "Prospect profile attached", status: "Ready for review" },
        humanReview: "Health advice, exercise prescription, injury concerns, and membership exceptions go to staff.",
      },
      {
        id: "trial-booking",
        tab: "Trial booking",
        title: "Prepare an approved first visit",
        inquiry: "Could I try the Tuesday beginner strength class?",
        response: "I can prepare the trial request and confirm the approved registration steps.",
        fields: [
          { label: "Program", value: "Beginner strength" },
          { label: "Session", value: "Tuesday · 6:30 PM" },
          { label: "Visit", value: "First trial" },
          { label: "Contact", value: "Text reminder" },
        ],
        nextStep: { label: "Prepared next step", title: "Trial visit request", detail: "Registration step included", status: "Ready to confirm" },
        humanReview: "Staff confirms capacity, readiness, coach matching, and policy exceptions.",
      },
      {
        id: "post-trial",
        tab: "Post-trial",
        title: "Keep follow-up consistent",
        inquiry: "I liked the class. What membership would include two visits per week?",
        response: "I can share approved membership information and prepare a conversation with the membership team.",
        fields: [
          { label: "Trial", value: "Completed" },
          { label: "Interest", value: "Two visits / week" },
          { label: "Program", value: "Strength" },
          { label: "Question", value: "Plan comparison" },
        ],
        nextStep: { label: "Prepared next step", title: "Membership follow-up", detail: "Trial context included", status: "Assigned" },
        humanReview: "Contracts, discounts, freezes, health concerns, and nonstandard terms remain with staff.",
      },
    ],
    pilotSignals: ["Time to first useful answer", "Prospect profile completeness", "Interest reaching a first visit"],
    handoffRules: ["Health or injury question", "Membership exception", "Prospect requests staff"],
    finalTitle: "Bring one repeated membership inquiry.",
    finalBody: "We’ll map the prospect fields, approved booking action, and staff handoff for a focused workflow review.",
  },
  "local-services": {
    family: "operations",
    headline: "Respond first. Turn each inquiry into a clear next step.",
    description: "ClawEase captures service type, location, scope, timing, and supporting details, then prepares an appointment, estimate request, or human follow-up for your team.",
    primaryCta: "Review my lead workflow",
    secondaryCta: "See an estimate-request example",
    capabilities: ["Immediate approved response", "Qualified job context", "Appointment, estimate, or handoff"],
    proof: [
      { label: "What it captures", title: "Service-fit context", body: "Service, location, scope, timing, supporting details, and preferred next action." },
      { label: "What it can prepare", title: "One concrete next step", body: "An appointment request, estimate request, or staff follow-up—not a dead-end inbox." },
      { label: "When a person takes over", title: "Pricing and exceptions", body: "Quotes, judgment, safety, unusual scope, and service exceptions stay with the team." },
    ],
    workflowEyebrow: "Sample local-service workflow",
    workflowTitle: "From high-intent inquiry to a defined owner and next step.",
    workflowBody: "The examples show three honest outcomes—appointment, estimate request, or staff follow-up—without pretending one automation fits every trade.",
    scenarios: [
      {
        id: "job-qualification",
        tab: "Qualify the job",
        title: "Collect the scope before follow-up",
        inquiry: "I need help replacing three exterior light fixtures at my home.",
        response: "I can collect the location, scope, timing, and supporting details your team needs.",
        fields: [
          { label: "Service", value: "Fixture replacement" },
          { label: "Location", value: "Residential" },
          { label: "Scope", value: "Three exterior fixtures" },
          { label: "Timing", value: "Within two weeks" },
        ],
        nextStep: { label: "Prepared next step", title: "Qualified service request", detail: "Scope and timing attached", status: "Ready for review" },
        humanReview: "Safety, code, technical diagnosis, and service-fit questions go to a qualified team member.",
      },
      {
        id: "estimate-request",
        tab: "Estimate request",
        title: "Prepare the information needed to quote",
        inquiry: "Can I send photos so you can tell me whether an on-site estimate is needed?",
        response: "Yes. I can collect approved photos and prepare the estimate request for review.",
        fields: [
          { label: "Request", value: "Estimate review" },
          { label: "Photos", value: "Three attached" },
          { label: "Access", value: "Front exterior" },
          { label: "Contact", value: "Email" },
        ],
        nextStep: { label: "Prepared next step", title: "Estimate request", detail: "Attachments included", status: "Staff review" },
        humanReview: "The business owns all pricing, diagnosis, site-visit, and feasibility decisions.",
      },
      {
        id: "appointment-request",
        tab: "Appointment request",
        title: "Offer a suitable service window",
        inquiry: "Thursday afternoon works if your team serves my area.",
        response: "I can check the approved service area and prepare the visit window for confirmation.",
        fields: [
          { label: "Service area", value: "Covered" },
          { label: "Day", value: "Thursday" },
          { label: "Window", value: "Afternoon" },
          { label: "Job", value: "Fixture replacement" },
        ],
        nextStep: { label: "Prepared next step", title: "Visit request", detail: "Pending team confirmation", status: "Window selected" },
        humanReview: "Capacity, technician fit, pricing, and schedule exceptions remain with the service team.",
      },
    ],
    pilotSignals: ["Speed to useful response", "Qualified request completeness", "Inquiries reaching a defined next step"],
    handoffRules: ["Safety or technical judgment", "Estimate or policy exception", "Customer requests staff"],
    finalTitle: "Bring one repeated customer inquiry.",
    finalBody: "We’ll map the qualifying fields, next-step options, and owner handoff for your specific service—not a generic template.",
  },
  "work-order-services": {
    family: "operations",
    headline: "Turn unstructured requests into owned, trackable work.",
    description: "ClawEase structures requester, location, asset, issue, impact, priority signals, access details, and attachments, then prepares the work for assignment and lifecycle updates.",
    primaryCta: "Review my work-order workflow",
    secondaryCta: "See a work-order example",
    capabilities: ["Standardized intake", "Transparent routing context", "Lifecycle updates with ownership"],
    proof: [
      { label: "What it captures", title: "A complete work record", body: "Requester, site, asset, issue, impact, priority signals, access, and evidence." },
      { label: "What it can prepare", title: "Owned operational work", body: "A structured work order, routing recommendation, or lifecycle update." },
      { label: "When a person takes over", title: "Priority and exceptions", body: "Safety, prioritization, assignment conflicts, approval, and closure judgment stay with people." },
    ],
    workflowEyebrow: "Sample work-order workflow",
    workflowTitle: "From inconsistent input to a transparent request lifecycle.",
    workflowBody: "This operations-focused example shows a work record, routing rules, and status history—not a booking interface.",
    scenarios: [
      {
        id: "work-order-intake",
        tab: "Standardize intake",
        title: "Turn an unstructured message into a record",
        inquiry: "The loading-bay door at Site 4 is stopping halfway and blocking deliveries.",
        response: "I can collect the site, asset, impact, access, evidence, and priority signals for operations.",
        fields: [
          { label: "Site", value: "Site 4 · Loading bay" },
          { label: "Asset", value: "Roll-up door" },
          { label: "Impact", value: "Deliveries blocked" },
          { label: "Evidence", value: "Video requested" },
        ],
        nextStep: { label: "Prepared next step", title: "Standard work order", detail: "Required fields assembled", status: "Ready for triage" },
        humanReview: "Safety impact, priority, shutdown, and emergency response are decided by operations staff.",
      },
      {
        id: "assignment",
        tab: "Set ownership",
        title: "Show why a request is routed",
        inquiry: "This needs someone who can service the door system and access the site before opening.",
        response: "I’ll attach the skill, site, access window, and impact signals to the routing recommendation.",
        fields: [
          { label: "Skill", value: "Commercial door systems" },
          { label: "Access", value: "Before 7 AM" },
          { label: "Impact", value: "Operational blockage" },
          { label: "Approval", value: "Required" },
        ],
        nextStep: { label: "Prepared next step", title: "Ownership recommendation", detail: "Rule context visible", status: "Coordinator review" },
        humanReview: "A coordinator approves priority, assignee, approval path, and any conflicting routing rule.",
      },
      {
        id: "lifecycle",
        tab: "Track lifecycle",
        title: "Keep requesters aligned with status",
        inquiry: "Has the work been assigned, and will deliveries be affected tomorrow?",
        response: "I can share approved lifecycle milestones and route planning questions to the owner.",
        fields: [
          { label: "Status", value: "Assigned" },
          { label: "Owner", value: "Facilities queue" },
          { label: "Milestone", value: "Site visit planned" },
          { label: "Exception", value: "Impact question flagged" },
        ],
        nextStep: { label: "Prepared next step", title: "Lifecycle update", detail: "Requester and work order linked", status: "Ready to publish" },
        humanReview: "Schedule commitments, operational impact, approval, and closure confirmation remain with the owner.",
      },
    ],
    pilotSignals: ["Required field consistency", "Time from intake to ownership", "Lifecycle updates published from status"],
    handoffRules: ["Safety or priority decision", "No routing rule resolves ownership", "Approval or exception is required"],
    finalTitle: "Bring one repeated operational request.",
    finalBody: "We’ll map the work-order fields, routing logic, status events, and human approval points.",
  },
};
